import { Alert, Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import React, { FunctionComponent, useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'

import { usePropertyListsQuery } from '../../../graphql/generated'
import { useAppSelector } from '../../../redux/hooks'
import {
  numberSorter,
  stringSorter,
  utcDateSorter,
} from '../../utils/generalTableHelpers'
import { sparsePagination } from '../../utils/layoutConst'
import { propertyListDetailPath } from '../../utils/paths'
import { useColumnFilters } from '../../utils/useColumnFilters'
import { Day } from '../DateFormat/DateFormat'
import { PropertyListTableAction } from '../PropertyListTableAction/PropertyListTableAction'
import { TableItemsCount } from '../TableItemsCount/TableItemsCount'

export interface PropertyListTableItem {
  id: string
  name: string
  description?: string | null
  count: number
  propertyType: string
  createdAt?: string | null
}

export const PropertyListTable: FunctionComponent = () => {
  const accountId = useAppSelector(state => state.myProfile.tenantId)

  const { searchFilter, dateFilter, propertyTypeFilter, rangeFilter } =
    useColumnFilters<PropertyListTableItem>()

  const [{ data, fetching, error }] = usePropertyListsQuery({
    variables: { accountId },
  })

  const dataSource = useMemo(() => {
    if (!data?.allPropertyLists?.nodes) return []
    return data.allPropertyLists.nodes as PropertyListTableItem[]
  }, [data])

  const columns: ColumnsType<PropertyListTableItem> = useMemo(
    () => [
      {
        title: 'Vytvořeno',
        dataIndex: 'createdAt',
        width: 160,
        sorter: utcDateSorter('createdAt'),
        ...dateFilter('createdAt'),
        render: createdAt => <Day>{createdAt}</Day>,
      },
      {
        title: 'Typ',
        dataIndex: 'propertyType',
        width: 120,
        ...propertyTypeFilter('propertyType'),
        sorter: stringSorter('propertyType'),
      },
      {
        title: 'Název',
        dataIndex: 'name',
        sorter: stringSorter('name'),
        ...searchFilter('name'),
        render: (name, record) => (
          <Link to={propertyListDetailPath(record.id)}>{name}</Link>
        ),
      },
      {
        title: 'Popis',
        dataIndex: 'description',
        ellipsis: true,
        ...searchFilter('description'),
        sorter: stringSorter('description'),
      },
      {
        title: 'Počet',
        dataIndex: 'count',
        width: 110,
        className: 'text-right',
        ...rangeFilter('count'),
        sorter: numberSorter('count'),
      },
      {
        title: '',
        dataIndex: 'id',
        width: 160,
        render: (_, record) => <PropertyListTableAction record={record} />,
      },
    ],
    [dateFilter, propertyTypeFilter, rangeFilter, searchFilter]
  )

  const summary = useCallback(
    () => (
      <Table.Summary.Row className="bg-[#fafafa]">
        <Table.Summary.Cell colSpan={6} index={0} className="!py-2">
          <TableItemsCount total={dataSource.length || 0} />
        </Table.Summary.Cell>
      </Table.Summary.Row>
    ),
    [dataSource.length]
  )

  if (error)
    return (
      <Alert type="error" message={'Chyba při načítání seznamů nemovitostí.'} />
    )

  return (
    <Table
      loading={fetching}
      columns={columns}
      dataSource={dataSource}
      size="small"
      pagination={sparsePagination}
      summary={summary}
      rowKey="id"
    />
  )
}
